import { Box, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { Scrollbar } from '@europrocurement/flexy-components';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { enqueueSnackbar } from 'notistack';
import { useDossierDocumentSignContext } from '../../../providers';
import PreviewOverlayHeader from '../PreviewOverlayHeader/PreviewOverlayHeader';
import DossierSignDetail from '../DossierSignDetail/DossierSignDetail';
import { useDocumentSign, useDocumentSignRender } from '../../../hooks';

type DossierSignDetailOverlayProps = {
    sxProps?: object;
};

const DossierSignDetailOverlay: React.FunctionComponent<DossierSignDetailOverlayProps> = function ({
    sxProps,
}) {
    const { palette, breakpoints } = useTheme();
    const { raiseDossier, downloadDocument } = useDocumentSign();
    const { getDossierColor } = useDocumentSignRender();
    const downMd = useMediaQuery(breakpoints.down('md'));
    const {
        selectedDossier: dossier,
        setSelectedDossier,
        isRaisable,
    } = useDossierDocumentSignContext();
    const actions = [];

    if (!dossier) {
        return null;
    }

    const dossierColor = getDossierColor(dossier);

    const handleClose = () => {
        setSelectedDossier(undefined);
    };

    const handleRaise = async (id: number): Promise<void> => {
        try {
            await raiseDossier(id);
            enqueueSnackbar('Les destinataires ont bien été relancés');
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lors de la relances des destinataires', {
                variant: 'error',
            });
        }
    };

    if (isRaisable(dossier.id)) {
        actions.push({
            label: 'Relancer les signataires',
            icon: 'user-clock' as IconName,
            onClick: () => handleRaise(dossier.id),
        });
    }

    return (
        <Box
            onClick={handleClose}
            sx={{
                width: '100%',
                height: '100%',
                position: 'fixed',
                top: 0,
                left: 0,
            }}
        >
            <Box
                onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
                sx={{
                    position: 'fixed',
                    top: '0',
                    right: '0',
                    maxWidth: downMd ? '100%' : '50%',
                    width: '100%',
                    height: '100vh',
                    background: palette.background.paper,
                    boxShadow: `1px 1px 15px 0px ${palette.grey[200]}`,
                    borderRadius: '12px',
                    zIndex: 1150,
                    display: 'flex',
                    flexDirection: 'column',
                    paddingTop: '72px',
                    ...sxProps,
                }}
            >
                <Scrollbar>
                    <PreviewOverlayHeader
                        title={dossier.subject}
                        icon="file-pdf"
                        color={palette[dossierColor]}
                        onClose={handleClose}
                    />
                    <DossierSignDetail
                        dossier={dossier}
                        actions={actions}
                        onDownloadDocument={downloadDocument}
                    />
                </Scrollbar>
            </Box>
        </Box>
    );
};

export default DossierSignDetailOverlay;
